import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { Section, Inner, SectionHeader, SectionTitle, SectionSubtitle, Divider, Badge, fadeInUp } from '../styles/v2.styled';
import devopsCloudThumb from '../../image/courses/course-devops-cloud.png';
import nodeBackendThumb from '../../image/courses/course-node-backend.png';
import reactTypescriptThumb from '../../image/courses/course-react-typescript.png';
import aiEngineeringThumb from '../../image/courses/course-ai-engineering.png';
import dataEngineeringThumb from '../../image/courses/course-data-engineering.png';
import web3ContractsThumb from '../../image/courses/course-web3-contracts.png';

// API 미응답시 기본값
const DEFAULT_COURSES = [
  { id: 'featured-react', title: '실무 React 마스터', description: 'React 18 + TypeScript 실전 강의. 훅, 상태관리, 성능최적화까지.', category: 'Frontend', thumbnail_url: reactTypescriptThumb, color: '#2563eb' },
  { id: 'featured-node', title: 'Node.js 백엔드 부트캠프', description: 'Express, PostgreSQL, 인증, 배포까지 한 번에.', category: 'Backend', thumbnail_url: nodeBackendThumb, color: '#0d9488' },
  { id: 'featured-devops', title: 'DevOps & 클라우드 입문', description: 'Docker, CI/CD, AWS 기본기를 4주에 끝내기.', category: 'DevOps', thumbnail_url: devopsCloudThumb, color: '#7c3aed' },
  { id: 'featured-ai', title: 'AI 애플리케이션 엔지니어링', description: 'LLM API, RAG, 자동화 워크플로우를 서비스에 적용합니다.', category: 'AI', thumbnail_url: aiEngineeringThumb, color: '#b91c1c' },
  { id: 'featured-data', title: '데이터 엔지니어링 실전', description: '수집, 정제, 분석 파이프라인과 대시보드 구축을 다룹니다.', category: 'Data', thumbnail_url: dataEngineeringThumb, color: '#0891b2' },
  { id: 'featured-web3', title: 'Web3 스마트컨트랙트 입문', description: '블록체인 구조, 지갑 연동, 컨트랙트 보안 기초를 익힙니다.', category: 'Web3', thumbnail_url: web3ContractsThumb, color: '#4f46e5' },
];

const COLOR_BY_CATEGORY = {
  Frontend: '#2563eb',
  Backend: '#0d9488',
  DevOps: '#7c3aed',
  AI: '#dc2626',
  Data: '#0891b2',
  Web3: '#4f46e5',
  default: '#0891b2',
};

function categoryColor(c) {
  return COLOR_BY_CATEGORY[c] || COLOR_BY_CATEGORY.default;
}

const resolveCourseThumb = (course, index) => {
  if (course.thumbnail_url) return course.thumbnail_url;
  if (/ai|llm|인공지능/i.test(course.title || course.category || '')) return aiEngineeringThumb;
  if (/data|데이터/i.test(course.title || course.category || '')) return dataEngineeringThumb;
  if (/web3|block|contract|블록|컨트랙트/i.test(course.title || course.category || '')) return web3ContractsThumb;
  if (/node|backend|백엔드/i.test(course.title || course.category || '')) return nodeBackendThumb;
  if (/react|frontend|프론트/i.test(course.title || course.category || '')) return reactTypescriptThumb;
  if (/devops|cloud|클라우드/i.test(course.title || course.category || '')) return devopsCloudThumb;
  return DEFAULT_COURSES[index % DEFAULT_COURSES.length].thumbnail_url;
};

const normalizeCourses = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) return DEFAULT_COURSES;

  const normalizedRows = rows.map((course, index) => ({
    ...course,
    thumbnail_url: resolveCourseThumb(course, index),
  }));
  const registeredKeys = new Set(
    normalizedRows.map((course) => course.title || course.id)
  );
  const supplements = DEFAULT_COURSES.filter((course) => !registeredKeys.has(course.title));

  return [...normalizedRows, ...supplements].slice(0, 6);
};

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 22px;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled(Link)`
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 320px;
  background: ${({ theme }) => theme.colors.neutral[0]};
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.neutral[200]};
  text-decoration: none;
  overflow: hidden;
  transition: all 0.25s ease;
  animation: ${fadeInUp} 0.5s ease both;
  animation-delay: ${({ $idx }) => $idx * 0.06}s;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 16px 36px rgba(17, 24, 39, 0.1);
    border-color: ${({ $color }) => $color};
  }
`;

const Thumb = styled.div`
  width: 100%;
  height: 160px;
  background-color: ${({ $color }) => $color};
  background-image: ${({ $url }) => $url ? `url(${$url})` : 'none'};
  background-size: cover;
  background-position: center;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 4px;
    background: ${({ $color }) => $color};
  }
`;

const CardBody = styled.div`
  padding: 20px 22px 22px;
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const CardTop = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: flex-start;
  margin-bottom: 16px;
`;

const CardMeta = styled.span`
  font-size: 0.82rem;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.neutral[500]};
`;

const CardTitle = styled.h3`
  font-family: ${({ theme }) => theme.typography.fontFamily.semibold};
  font-size: 1.1rem;
  font-weight: 900;
  color: ${({ theme }) => theme.colors.neutral[900]};
  line-height: 1.35;
  margin: 0 0 12px;
`;

const CardDesc = styled.p`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.neutral[500]};
  line-height: 1.6;
  margin: 0 0 20px;
  flex: 1;
`;

const CardLink = styled.span`
  font-size: 0.88rem;
  font-weight: 900;
  color: ${({ theme }) => theme.colors.primary[700]};
  -webkit-text-fill-color: ${({ theme }) => theme.colors.primary[700]};
`;

function CourseCards() {
  const [courses, setCourses] = useState(DEFAULT_COURSES);

  useEffect(() => {
    fetch('/lms/courses')
      .then((r) => r.ok ? r.json() : null)
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) {
          setCourses(normalizeCourses(data));
        }
      })
      .catch(() => { /* keep defaults */ });
  }, []);

  return (
    <Section $py="72px" $pyMobile="48px">
      <Inner>
        <SectionHeader>
          <Divider $center />
          <SectionTitle>대표 교육과정</SectionTitle>
          <SectionSubtitle>강사가 직접 설계한 프로젝트 중심 커리큘럼입니다.</SectionSubtitle>
        </SectionHeader>

        <Grid>
          {courses.map((c, i) => {
            const color = c.color || categoryColor(c.category);
            return (
              <Card to="/Courses" key={`${c.id}-${i}`} $idx={i} $color={color}>
                <Thumb $url={c.thumbnail_url} $color={color} data-testid="featured-course-thumbnail" />
                <CardBody>
                  <CardTop>
                    <CardMeta>{c.category || '교육과정'}</CardMeta>
                    {c.status === 'open' && <Badge $variant="success">모집중</Badge>}
                  </CardTop>
                  <CardTitle>{c.title}</CardTitle>
                  <CardDesc>{c.description}</CardDesc>
                  <CardLink>자세히 보기 →</CardLink>
                </CardBody>
              </Card>
            );
          })}
        </Grid>
      </Inner>
    </Section>
  );
}

export default CourseCards;
